import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import styled, { ThemeProvider } from 'styled-components';
import { QueryClient, QueryClientProvider } from 'react-query';
import { Toaster } from 'react-hot-toast';
import { HelmetProvider } from 'react-helmet-async';

// Store
import { useAuthStore } from './store/authStore';
import { decodeTokenExpiration } from './utils/tokenUtils';
import { useCart } from './store/cartStore';


// Real Components - Adding back systematically  
import LoadingSpinner from './components/LoadingSpinner';
import ProtectedRoute from './components/ProtectedRoute';
import Navbar from './components/Navbar';
import SessionManager from './components/SessionManager';
import { AlertProvider } from './contexts/AlertContext';
import Footer from './components/Footer';

// Additional Components (keeping imports available for pages that need them)
// import { Button as ComponentButton } from './components/Button';
// import Card from './components/Card';
// import Input from './components/Input';

// Pages - Adding back ALL components
import HomePage from './pages/HomePage';
import EventsPage from './pages/EventsPage';
import EventDetailsPage from './pages/EventDetailsPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import CartPage from './pages/CartPage';
import CheckoutSuccessPage from './pages/CheckoutSuccessPage';
import CheckoutCancelPage from './pages/CheckoutCancelPage';
import CheckoutPage from './pages/CheckoutPage';
import ProfilePage from './pages/ProfilePage';
import AdminRoutes from './pages/AdminRoutes';
import DashboardPage from './pages/Dashboard/DashboardPage';
import ResetPasswordPage from './pages/ResetPasswordPage';

import VenueCreationWizard from './pages/VenueCreationWizard';
import EventCreationWizard from './pages/EventCreationWizard';
import QRScannerPage from './pages/QRScannerPage';
import NotFoundPage from './pages/NotFoundPage';
import LegalPage from './pages/LegalPage';
import PosDashboardPage from './pages/Pos/PosDashboardPage';
import PosTerminalPage from './pages/Pos/PosTerminalPage';
import RoleRequestPage from './pages/RoleRequestPage';

// Create a QueryClient
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
});

// Theme
const theme = {
  colors: {
    primary: '#007AFF',
    secondary: '#5AC8FA',
    background: '#f0f0f0',
    text: '#1C1C1E',
  },
  spacing: {
    sm: '0.5rem',
    md: '1rem',
    lg: '1.5rem',
  },
};

// Styled Components
const AppWrapper = styled.div`
  font-family: Arial, sans-serif;
  min-height: 100vh;
  background: transparent;
  display: flex;
  flex-direction: column;
`;



const MainContent = styled.main`
  flex: 1;
  width: 100%;
  display: flex;
  flex-direction: column;
`;





const ScrollToTop = () => {
  const location = useLocation();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, [location.pathname]);

  return null;
};

const FooterWrapper = () => {
  const location = useLocation();
  const hideFooter = location.pathname.startsWith('/profile');
  return hideFooter ? null : <Footer />;
};

// Test Routes for components we haven't added back yet
const TestHomePage = () => (
  <div style={{ padding: '20px' }}>
    <h1>Testing Real Components</h1>
    <p>This page will help us test if the real HomePage component works.</p>
    <p>If you see this, we're using the test component. The real HomePage should replace this.</p>
  </div>
);

const App = () => {
  const [appLoading, setAppLoading] = useState(true);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const authToken = useAuthStore((state) => state.token);


  useEffect(() => {
    let isMounted = true;

    const initializeAuth = async () => {
      const token = localStorage.getItem('authToken');
      const cachedUser = localStorage.getItem('user');
      const cachedExpiration = localStorage.getItem('tokenExpiresAt');
      const cachedSessionStart = localStorage.getItem('sessionStartedAt');

      if (!token || !cachedUser) {
        if (isMounted) {
          setAppLoading(false);
        }
        return;
      }

      try {
        const parsedUser = JSON.parse(cachedUser);
        const decodedExpiration = cachedExpiration ? Number(cachedExpiration) : decodeTokenExpiration(token);
        const tokenExpiresAt = Number.isFinite(decodedExpiration) ? decodedExpiration : null;
        const sessionStartedAt = Number.isFinite(Number(cachedSessionStart))
          ? Number(cachedSessionStart)
          : Date.now();

        if (tokenExpiresAt && Date.now() >= tokenExpiresAt) {
          await useAuthStore.getState().logout(true);
        } else {
          useAuthStore.setState({
            user: parsedUser,
            token,
            tokenExpiresAt,
            sessionStartedAt,
            isAuthenticated: true,
            isLoading: false,
            error: null,
          });
          localStorage.setItem('sessionStartedAt', String(sessionStartedAt));
          // Pull current cart reservations from backend so cart works across browsers/devices.
          await useCart.getState().loadCartFromBackend();
        }
      } catch (error) {
        console.warn('App failed to parse cached user:', error);
      } finally {
        if (isMounted) {
          setAppLoading(false);
        }
      }
    };

    initializeAuth();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    // Keep cart in sync with auth state (important for cross-browser cart).
    if (isAuthenticated && authToken) {
      useCart.getState().loadCartFromBackend();
    } else {
      useCart.setState({ cartItems: [], error: null, validationResults: {}, lastValidationTime: null });
    }
  }, [isAuthenticated, authToken]);

  // handleLogout now handled by WorkingNavbar

  if (appLoading) {
    return <LoadingSpinner fullScreen text="Initializing TikPlat..." />;
  }

  return (
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider theme={theme}>
          <AlertProvider>
            <Router>
              <ScrollToTop />
              <AppWrapper>
                <SessionManager />
                {/* Updated Navbar with Role-Specific Dashboards */}
                <Navbar />

                <MainContent>
                  {/* ALL Routes - Complete Functionality Restored */}
                  <Routes>
                    {/* Public Routes */}
                    <Route path="/" element={<HomePage />} />
                    <Route path="/events" element={<EventsPage />} />
                    <Route path="/events/:id" element={<EventDetailsPage />} />
                    <Route path="/login" element={<LoginPage />} />
                    <Route path="/register" element={<RegisterPage />} />
                    <Route path="/reset-password" element={<ResetPasswordPage />} />

                    {/* User Routes - Protected */}
                    <Route
                      path="/profile"
                      element={
                        <ProtectedRoute>
                          <ProfilePage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/dashboard/*"
                      element={
                        <ProtectedRoute>
                          <DashboardPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/cart"
                      element={
                        <ProtectedRoute>
                          <CartPage />
                        </ProtectedRoute>
                      }
                    />
                    {/* Checkout Pages */}
                    <Route
                      path="/checkout"
                      element={
                        <ProtectedRoute>
                          <CheckoutPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/checkout/success"
                      element={
                        <ProtectedRoute>
                          <CheckoutSuccessPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/checkout/cancel"
                      element={
                        <ProtectedRoute>
                          <CheckoutCancelPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/roles/:type"
                      element={
                        <ProtectedRoute>
                          <RoleRequestPage />
                        </ProtectedRoute>
                      }
                    />

                    {/* Admin Routes - Admin Only */}
                    <Route
                      path="/admin/*"
                      element={
                        <ProtectedRoute requiredRole="ADMIN">
                          <AdminRoutes />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin"
                      element={
                        <ProtectedRoute requiredRole="ADMIN">
                          <Navigate to="/dashboard" replace />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/events/create"
                      element={
                        <ProtectedRoute requiredRole="ADMIN">
                          <EventCreationWizard />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/events/:id"
                      element={
                        <ProtectedRoute requiredRole="ADMIN">
                          <EventCreationWizard />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/venues/create"
                      element={
                        <ProtectedRoute requiredRole="ADMIN">
                          <VenueCreationWizard />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/venues/:id"
                      element={
                        <ProtectedRoute requiredRole="ADMIN">
                          <VenueCreationWizard />
                        </ProtectedRoute>
                      }
                    />

                    <Route
                      path="/planner/events/:id"
                      element={
                        <ProtectedRoute requiredRole={["PLANNER", "ADMIN"]}>
                          <EventCreationWizard />
                        </ProtectedRoute>
                      }
                    />

                    {/* Planner Routes - Planner or Admin */}
                    <Route
                      path="/planner/*"
                      element={
                        <ProtectedRoute requiredRole={["PLANNER", "ADMIN"]}>
                          <DashboardPage forcedRole="PLANNER" />
                        </ProtectedRoute>
                      }
                    />

                    {/* Staff Routes - Staff Only */}
                    <Route
                      path="/staff/*"
                      element={
                        <ProtectedRoute requiredRole="STAFF">
                          <DashboardPage forcedRole="STAFF" />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/scan"
                      element={
                        <ProtectedRoute requiredRole={["STAFF", "ADMIN", "PLANNER"]}>
                          <QRScannerPage />
                        </ProtectedRoute>
                      }
                    />

                    {/* POS Routes - Sale Agents Only */}
                    <Route
                      path="/pos"
                      element={
                        <ProtectedRoute requiredRole="SALE_AGENT">
                          <PosDashboardPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/pos/event/:eventId"
                      element={
                        <ProtectedRoute requiredRole="SALE_AGENT">
                          <PosTerminalPage />
                        </ProtectedRoute>
                      }
                    />

                    {/* Test/Development Routes */}
                    <Route path="/test-home" element={<TestHomePage />} />
                    <Route path="/legal/*" element={<LegalPage />} />
                    <Route
                      path="/protected-test"
                      element={
                        <ProtectedRoute>
                          <div style={{ padding: '20px' }}>
                            <h1>Protected Page</h1>
                            <p>This page requires authentication!</p>
                            <p>If you can see this, ProtectedRoute is working.</p>
                          </div>
                        </ProtectedRoute>
                      }
                    />

                    {/* 404 Route */}
                    <Route path="*" element={<NotFoundPage />} />
                  </Routes>
                </MainContent>
                <FooterWrapper />
              </AppWrapper>
            </Router>
          </AlertProvider>

          {/* Toast notifications */}
          <Toaster
            position="top-right"
            toastOptions={{
              duration: 4000,
              style: {
                background: 'white',
                color: '#333',
                border: '1px solid #ddd',
                borderRadius: '8px',
                padding: '12px 16px',
                fontSize: '14px',
              },
              success: {
                iconTheme: {
                  primary: '#34C759',
                  secondary: 'white',
                },
              },
              error: {
                iconTheme: {
                  primary: '#FF3B30',
                  secondary: 'white',
                },
              },
            }}
          />
        </ThemeProvider>
      </QueryClientProvider>
    </HelmetProvider>
  );
};

export default App;
